<template>
  <!--
    ── La primera pantalla del asistente ────────────────────────────────────
    Para empezar a trabajar, no para leer: la acción principal de lo instalado
    (la marca el módulo con `primary`; en una empresa con órdenes de trabajo es
    «Crear una orden») como una caja donde se cuenta lo que hay que hacer, los
    demás atajos, lo que está pasando ahora (las cifras destacadas del inicio,
    que son de los módulos y no de aquí) y las conversaciones a medias.
  -->
  <div class="home">
    <!-- «Hoy»: lo que los avisos de los módulos tienen para esta persona, cada
         cosa con su enlace. Lo mismo que el resumen de la mañana, pedido ahora. -->
    <section v-if="todayLines.length || store.todayFailed" class="today" :aria-label="t('assistant.today.title')">
      <p class="today__title">{{ t('assistant.today.title') }}</p>
      <p v-if="store.todayFailed" class="failed" role="status">
        {{ t('assistant.today.failed') }}
        <button type="button" class="linkish" @click="store.loadToday()">{{ t('assistant.retry') }}</button>
      </p>
      <ul v-else class="today__list">
        <li v-for="line in todayLines" :key="line.key" class="today__item" :class="`today__item--${line.level}`">
          <RouterLink v-if="line.link" :to="line.link" class="today__link">{{ line.text }}</RouterLink>
          <span v-else>{{ line.text }}</span>
          <!-- La pregunta se prepara en la caja y no se manda: se lee antes. -->
          <button
            v-if="line.ask"
            type="button"
            class="today__ask"
            :disabled="!store.canAsk"
            @click="store.prepare(line.ask.question)"
          >{{ line.ask.label || line.ask.question }}</button>
        </li>
      </ul>
      <!-- La misma preferencia de avisos que obedece la campana (categoría
           «Resumen de cada mañana»): quien la apaga deja de recibirlo. -->
      <button type="button" class="linkish today__mute" :disabled="savingDigest" @click="toggleDigest">
        {{ store.digestMuted ? t('assistant.today.unmute') : t('assistant.today.mute') }}
      </button>
    </section>

    <section v-if="primary" class="start">
      <p class="start__title">{{ opener(primary) }}</p>
      <form class="start__form" @submit.prevent="startPrimary">
        <UiTextarea
          v-model="request"
          :rows="3"
          :placeholder="t('assistant.home.describe')"
          :aria-label="opener(primary)"
          @keydown.enter.exact="onEnter"
        />
        <!-- Llamar ya está en la cabecera del panel: dos botones iguales en
             la misma pantalla obligan a pensar si hacen cosas distintas. -->
        <div class="start__buttons">
          <UiButton type="submit" :disabled="!store.canAsk">{{ request.trim() ? t('assistant.home.go') : t('assistant.home.guideMe') }}</UiButton>
        </div>
      </form>
      <p class="start__hint">{{ t('assistant.home.hint') }}</p>
    </section>

    <section v-if="others.length" class="block">
      <p class="block__title">{{ t('assistant.suggestions') }}</p>
      <div class="quick">
        <button v-for="suggestion in others" :key="suggestion.textKey" type="button" class="quick__item" :disabled="!store.canAsk" @click="store.send(opener(suggestion))">
          {{ opener(suggestion) }}
        </button>
      </div>
    </section>

    <section v-if="loadingNow || now.length || nowFailed" class="block">
      <p class="block__title">{{ t('assistant.home.now') }}</p>
      <p v-if="nowFailed && !loadingNow" class="failed" role="status">
        {{ t('assistant.home.nowFailed') }}
        <button type="button" class="linkish" @click="loadNow">{{ t('assistant.retry') }}</button>
      </p>
      <div v-if="loadingNow" class="kpis">
        <UiSkeleton v-for="n in 3" :key="n" height="64px" />
      </div>
      <div v-else class="kpis">
        <component
          :is="card.route ? 'RouterLink' : 'div'"
          v-for="card in now" :key="card.id"
          :to="card.route || undefined"
          class="kpi" :class="{ 'kpi--danger': card.tone === 'danger' }"
        >
          <span class="kpi__value">{{ card.failed ? '—' : card.value }}</span>
          <span class="kpi__label">{{ t(card.labelKey) }}</span>
        </component>
      </div>
    </section>

    <section v-if="recent.length" class="block">
      <p class="block__title">{{ t('assistant.home.recent') }}</p>
      <button v-for="conversation in recent" :key="conversation._id" type="button" class="recent" @click="store.openConversation(conversation._id)">
        <span class="truncate">{{ conversationTitle(conversation, { t, te }) }}</span>
        <span class="recent__when">{{ formatRelative(conversation.updatedAt) }}</span>
      </button>
    </section>

    <p v-if="!primary && !others.length && !now.length && !loadingNow && !todayLines.length" class="empty">{{ t('assistant.emptyHint') }}</p>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { UiButton, UiSkeleton, UiTextarea, api, formatRelative, useToast, errorMessage } from '@general/ui-kit'

import { useAssistantStore } from '../store'
import { composeToday } from '../today'
import { conversationTitle } from '../conversationTitle'

const { t, te, locale } = useI18n()
const store = useAssistantStore()
const toast = useToast()

// ── Hoy ──────────────────────────────────────────────────────────────────────
const todayLines = computed(() => composeToday(store.today?.items ?? [], {
  t, te, locale: locale.value, today: store.today?.day ?? null,
}).lines)

const savingDigest = ref(false)
async function toggleDigest() {
  savingDigest.value = true
  try {
    await store.setDigestMuted(!store.digestMuted)
  } catch (error) {
    toast.error(errorMessage(error, t))
  } finally {
    savingDigest.value = false
  }
}

const opener = (suggestion) => (suggestion.textKey && te(suggestion.textKey) ? t(suggestion.textKey) : suggestion.text)

const primary = computed(() => store.suggestions.find(suggestion => suggestion.primary) ?? null)
// Los del mismo módulo que la acción principal primero, y los que están en el
// idioma de quien mira antes que los que solo traen el inglés de respaldo.
const others = computed(() => store.suggestions
  .filter(suggestion => !suggestion.primary)
  .map(suggestion => ({
    suggestion,
    rank: (suggestion.module === primary.value?.module ? 0 : 2) + (suggestion.textKey && te(suggestion.textKey) ? 0 : 1),
  }))
  .sort((a, b) => a.rank - b.rank)
  .slice(0, 4)
  .map(entry => entry.suggestion))
const recent = computed(() => (store.conversations ?? []).slice(0, 3))

// ── Empezar por la acción principal ────────────────────────────────────────
const request = ref('')

function startPrimary() {
  if (!store.canAsk) return
  const said = request.value.trim()
  // Con lo que hay que hacer escrito, va entero en la primera frase; sin nada,
  // la acción sola, y el asistente pregunta con un widget lo que falte.
  store.send(said ? `${opener(primary.value)}: ${said}` : opener(primary.value))
  request.value = ''
}

// Intro envía con teclado de ordenador; con el del teléfono es salto de línea,
// como en la caja del chat (AssistantPanel), y se envía con el botón.
const touch = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
function onEnter(event) {
  if (touch) return
  event.preventDefault()
  startPrimary()
}

// ── Lo que está pasando ahora ──────────────────────────────────────────────
// Las cifras destacadas del inicio que esta persona puede ver, del módulo de
// la acción principal primero. Pedidas una a una: una que falla dice «—» y
// las demás siguen.
const now = ref([])
const loadingNow = ref(true)
// Las cifras no llegaron (sin red, el servidor): se dice, y se puede reintentar.
const nowFailed = ref(false)

async function loadNow() {
  loadingNow.value = true
  nowFailed.value = false
  try {
    const { widgets } = await api.get('/dashboard/catalog')
    const module = primary.value?.module
    const picked = (widgets ?? [])
      .filter(widget => widget.kind === 'kpi' && (widget.featured || widget.module === module))
      .sort((a, b) => Number(b.module === module) - Number(a.module === module))
      .slice(0, 3)
    now.value = await Promise.all(picked.map(async (widget) => {
      try {
        const result = await api.get(`/dashboard/widgets/${encodeURIComponent(widget.id)}`)
        return { ...widget, value: result.data?.value ?? 0, tone: result.data?.tone ?? '', failed: result.status !== 'ok' }
      } catch {
        return { ...widget, value: '—', failed: true }
      }
    }))
  } catch (error) {
    // Sin dashboard en esta instancia, o sin permiso: el bloque no se dibuja.
    // Sin red o con el servidor caído, sí: se dice y se puede reintentar.
    now.value = []
    nowFailed.value = !error?.status || error.status >= 500
  } finally {
    loadingNow.value = false
  }
}

onMounted(() => {
  loadNow()
  store.loadToday()
  store.loadDigestPreference()
})
</script>

<style scoped>
.home { display: flex; flex-direction: column; gap: var(--space-4); padding: var(--space-4); }

.start {
  border: 1px solid var(--color-primary);
  background: var(--color-primary-soft);
  border-radius: var(--radius);
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.start__title { margin: 0; font-weight: 600; font-size: 1.05rem; }
.start__form { display: flex; flex-direction: column; gap: var(--space-2); }
/* Sin el tirador de redimensionar: en un teléfono es una esquina que no se
   puede coger y que parece rota. */
.start__form :deep(textarea) { resize: none; }
.start__buttons { display: flex; flex-wrap: wrap; gap: var(--space-2); }
.start__buttons > :first-child { flex: 1; }
.start__hint { margin: 0; font-size: var(--text-xs); color: var(--color-text-soft); }

.today {
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.today__title { margin: 0; font-weight: 600; }
.today__list { margin: 0; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 6px; }
.today__item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 10px;
  font-size: var(--text-sm);
  padding-left: 12px;
  position: relative;
}
.today__item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0.55em;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-text-mute);
}
.today__item--warning::before { background: var(--color-warning); }
.today__item--danger::before { background: var(--color-danger); }
.today__link { color: inherit; text-decoration: underline; text-underline-offset: 2px; }
.today__ask {
  border: 1px solid var(--color-primary);
  background: var(--color-primary-soft);
  color: inherit;
  font: inherit;
  font-size: var(--text-xs);
  border-radius: var(--radius-sm);
  padding: 4px 10px;
  min-height: 32px;
  cursor: pointer;
}
.today__ask:disabled { opacity: 0.6; cursor: default; }
.today__mute { align-self: flex-start; font-size: var(--text-xs); color: var(--color-text-mute); }

.block { display: flex; flex-direction: column; gap: var(--space-2); }
.block__title { margin: 0; font-size: var(--text-xs); font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-mute); }

.quick { display: flex; flex-direction: column; gap: 6px; }
.quick__item {
  text-align: left;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
  min-height: 44px;
}
.quick__item:hover:not(:disabled) { border-color: var(--color-primary); }
.quick__item:disabled { opacity: 0.6; cursor: default; }

.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(110px, 100%), 1fr)); gap: var(--space-2); }
.kpi {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface);
  color: inherit;
  text-decoration: none;
}
.kpi__value { font-size: 1.4rem; font-weight: 600; line-height: 1.1; }
.kpi__label { font-size: var(--text-xs); color: var(--color-text-mute); }
.kpi--danger .kpi__value { color: var(--color-danger); }

.recent {
  display: flex;
  justify-content: space-between;
  gap: var(--space-2);
  padding: 8px 4px;
  background: none;
  border: 0;
  border-bottom: 1px solid var(--color-border);
  color: inherit;
  font: inherit;
  font-size: var(--text-sm);
  text-align: left;
  cursor: pointer;
}
.recent__when { flex: none; color: var(--color-text-mute); font-size: var(--text-xs); }
.empty { color: var(--color-text-mute); text-align: center; }
.failed { margin: 0; font-size: var(--text-sm); color: var(--color-text-soft); display: flex; flex-wrap: wrap; gap: 6px; align-items: center; }
.linkish { background: none; border: 0; padding: 0; color: var(--color-primary); font: inherit; cursor: pointer; text-decoration: underline; min-height: 32px; }
</style>
